/**
 * A one-sheet .xlsx, written by hand.
 *
 * An .xlsx is a zip of a handful of XML files. The libraries that write one
 * carry a whole spreadsheet model; a table export needs one sheet of values,
 * a bold header and a date format, which is a few strings and a zip with no
 * compression ("stored"). Excel, LibreOffice, Numbers and Google Sheets all
 * open it. Strings are inline, so nothing a cell holds is ever read as a
 * formula.
 */

export type XlsxCell = string | number | boolean | Date | null | undefined;

const MAIN_NS = "http://schemas.openxmlformats.org/spreadsheetml/2006/main";
const REL_NS = "http://schemas.openxmlformats.org/package/2006/relationships";
const DOC_REL = "http://schemas.openxmlformats.org/officeDocument/2006/relationships";
const XML_HEAD = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n';

/** Style indexes into `cellXfs` below. */
const STYLE_HEADER = 1;
const STYLE_DATE = 2;

function escapeXml(text: string): string {
  return (
    text
      // Control characters are not allowed in XML 1.0 at all; Excel refuses the file.
      // eslint-disable-next-line no-control-regex
      .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F￾￿]/g, "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
  );
}

/** A, B, … Z, AA, AB, … */
function columnLetter(index: number): string {
  let n = index + 1;
  let out = "";
  while (n > 0) {
    const rem = (n - 1) % 26;
    out = String.fromCharCode(65 + rem) + out;
    n = Math.floor((n - 1) / 26);
  }
  return out;
}

/** Sheet names: at most 31 characters, none of []:*?/\ . */
function sheetName(name: string): string {
  const clean = name
    .replace(/[[\]:*?/\\]/g, " ")
    .trim()
    .slice(0, 31);
  return clean || "Sheet1";
}

/**
 * A date as Excel's serial day number. Excel has no timezone: it shows the
 * wall-clock time it is handed, so `utc` picks which wall clock that is.
 */
function excelSerial(date: Date, utc: boolean): number {
  const wallMs = utc ? date.getTime() : date.getTime() - date.getTimezoneOffset() * 60_000;
  return wallMs / 86_400_000 + 25_569;
}

function cellXml(ref: string, value: XlsxCell, utc: boolean, style?: number): string {
  const s = style !== undefined ? ` s="${style}"` : "";
  if (value === null || value === undefined || value === "") return "";
  if (value instanceof Date) {
    if (!Number.isFinite(value.getTime())) return "";
    return `<c r="${ref}" s="${STYLE_DATE}"><v>${excelSerial(value, utc)}</v></c>`;
  }
  if (typeof value === "number") {
    return Number.isFinite(value) ? `<c r="${ref}"${s}><v>${value}</v></c>` : "";
  }
  if (typeof value === "boolean") return `<c r="${ref}"${s} t="b"><v>${value ? 1 : 0}</v></c>`;
  const space = /^\s|\s$/.test(value) ? ' xml:space="preserve"' : "";
  return `<c r="${ref}"${s} t="inlineStr"><is><t${space}>${escapeXml(value)}</t></is></c>`;
}

function displayLength(value: XlsxCell): number {
  if (value === null || value === undefined) return 0;
  if (value instanceof Date) return 16;
  return String(value).length;
}

function sheetXml(headers: string[], rows: XlsxCell[][], utc: boolean): string {
  const widths = headers.map((h, c) =>
    Math.min(50, Math.max(8, h.length + 2, ...rows.slice(0, 500).map((r) => displayLength(r[c]) + 2))),
  );
  const cols = widths
    .map((w, i) => `<col min="${i + 1}" max="${i + 1}" width="${w}" customWidth="1"/>`)
    .join("");
  const lines: string[] = [];
  lines.push(
    `<row r="1">${headers.map((h, c) => cellXml(`${columnLetter(c)}1`, h, utc, STYLE_HEADER)).join("")}</row>`,
  );
  rows.forEach((row, i) => {
    const r = i + 2;
    lines.push(
      `<row r="${r}">${row.map((v, c) => cellXml(`${columnLetter(c)}${r}`, v, utc)).join("")}</row>`,
    );
  });
  const last = `${columnLetter(Math.max(0, headers.length - 1))}${rows.length + 1}`;
  return (
    `${XML_HEAD}<worksheet xmlns="${MAIN_NS}" xmlns:r="${DOC_REL}">` +
    `<dimension ref="A1:${last}"/>` +
    // The header row stays put while the rows scroll under it.
    '<sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>' +
    `<cols>${cols}</cols>` +
    `<sheetData>${lines.join("")}</sheetData>` +
    (headers.length > 0 ? `<autoFilter ref="A1:${last}"/>` : "") +
    "</worksheet>"
  );
}

function workbookXml(name: string, headerCount: number, rowCount: number): string {
  const last = `$${columnLetter(Math.max(0, headerCount - 1))}$${rowCount + 1}`;
  const quoted = `'${name.replace(/'/g, "''")}'`;
  return (
    `${XML_HEAD}<workbook xmlns="${MAIN_NS}" xmlns:r="${DOC_REL}">` +
    `<sheets><sheet name="${escapeXml(name)}" sheetId="1" r:id="rId1"/></sheets>` +
    // Excel keeps an autofilter's range here as well as on the sheet.
    `<definedNames><definedName name="_xlnm._FilterDatabase" localSheetId="0" hidden="1">${escapeXml(quoted)}!$A$1:${last}</definedName></definedNames>` +
    "</workbook>"
  );
}

const STYLES_XML =
  `${XML_HEAD}<styleSheet xmlns="${MAIN_NS}">` +
  '<numFmts count="1"><numFmt numFmtId="164" formatCode="yyyy-mm-dd hh:mm"/></numFmts>' +
  '<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts>' +
  '<fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills>' +
  '<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>' +
  '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>' +
  '<cellXfs count="3">' +
  '<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>' +
  '<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/>' +
  '<xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>' +
  "</cellXfs>" +
  '<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>' +
  "</styleSheet>";

const CONTENT_TYPES_XML =
  `${XML_HEAD}<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">` +
  '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>' +
  '<Default Extension="xml" ContentType="application/xml"/>' +
  '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>' +
  '<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>' +
  '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>' +
  "</Types>";

const ROOT_RELS_XML =
  `${XML_HEAD}<Relationships xmlns="${REL_NS}">` +
  `<Relationship Id="rId1" Type="${DOC_REL}/officeDocument" Target="xl/workbook.xml"/>` +
  "</Relationships>";

const WORKBOOK_RELS_XML =
  `${XML_HEAD}<Relationships xmlns="${REL_NS}">` +
  `<Relationship Id="rId1" Type="${DOC_REL}/worksheet" Target="worksheets/sheet1.xml"/>` +
  `<Relationship Id="rId2" Type="${DOC_REL}/styles" Target="styles.xml"/>` +
  "</Relationships>";

export const XLSX_MEDIA_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

/** The workbook's bytes: one sheet, a bold frozen header with a filter, then the rows. */
export function xlsxBytes(
  name: string,
  headers: string[],
  rows: XlsxCell[][],
  options: { utc?: boolean } = {},
): Uint8Array {
  const title = sheetName(name);
  const utc = options.utc ?? false;
  return zipStored([
    ["[Content_Types].xml", CONTENT_TYPES_XML],
    ["_rels/.rels", ROOT_RELS_XML],
    ["xl/workbook.xml", workbookXml(title, headers.length, rows.length)],
    ["xl/_rels/workbook.xml.rels", WORKBOOK_RELS_XML],
    ["xl/styles.xml", STYLES_XML],
    ["xl/worksheets/sheet1.xml", sheetXml(headers, rows, utc)],
  ]);
}

// --- zip, stored --------------------------------------------------------------------------------

let crcTable: Uint32Array | null = null;

function crc32(bytes: Uint8Array): number {
  if (!crcTable) {
    crcTable = new Uint32Array(256);
    for (let n = 0; n < 256; n++) {
      let c = n;
      for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
      crcTable[n] = c >>> 0;
    }
  }
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) crc = crcTable[(crc ^ bytes[i]!) & 0xff]! ^ (crc >>> 8);
  return (crc ^ 0xffffffff) >>> 0;
}

/** A zip with every entry stored uncompressed — all an .xlsx reader requires. */
export function zipStored(files: [name: string, content: string][]): Uint8Array {
  const encoder = new TextEncoder();
  const entries = files.map(([name, content]) => {
    const nameBytes = encoder.encode(name);
    const data = encoder.encode(content);
    return { nameBytes, data, crc: crc32(data) };
  });
  const localSize = entries.reduce((n, e) => n + 30 + e.nameBytes.length + e.data.length, 0);
  const centralSize = entries.reduce((n, e) => n + 46 + e.nameBytes.length, 0);
  const out = new Uint8Array(localSize + centralSize + 22);
  const view = new DataView(out.buffer);
  // 1980-01-01 00:00, the zip epoch: the files have no meaningful timestamp.
  const dosDate = (0 << 9) | (1 << 5) | 1;
  const UTF8_NAMES = 0x0800;

  let offset = 0;
  const offsets: number[] = [];
  for (const e of entries) {
    offsets.push(offset);
    view.setUint32(offset, 0x04034b50, true);
    view.setUint16(offset + 4, 20, true);
    view.setUint16(offset + 6, UTF8_NAMES, true);
    view.setUint16(offset + 8, 0, true);
    view.setUint16(offset + 10, 0, true);
    view.setUint16(offset + 12, dosDate, true);
    view.setUint32(offset + 14, e.crc, true);
    view.setUint32(offset + 18, e.data.length, true);
    view.setUint32(offset + 22, e.data.length, true);
    view.setUint16(offset + 26, e.nameBytes.length, true);
    view.setUint16(offset + 28, 0, true);
    out.set(e.nameBytes, offset + 30);
    out.set(e.data, offset + 30 + e.nameBytes.length);
    offset += 30 + e.nameBytes.length + e.data.length;
  }

  const centralStart = offset;
  entries.forEach((e, i) => {
    view.setUint32(offset, 0x02014b50, true);
    view.setUint16(offset + 4, 20, true);
    view.setUint16(offset + 6, 20, true);
    view.setUint16(offset + 8, UTF8_NAMES, true);
    view.setUint16(offset + 10, 0, true);
    view.setUint16(offset + 12, 0, true);
    view.setUint16(offset + 14, dosDate, true);
    view.setUint32(offset + 16, e.crc, true);
    view.setUint32(offset + 20, e.data.length, true);
    view.setUint32(offset + 24, e.data.length, true);
    view.setUint16(offset + 28, e.nameBytes.length, true);
    view.setUint16(offset + 30, 0, true);
    view.setUint16(offset + 32, 0, true);
    view.setUint16(offset + 34, 0, true);
    view.setUint16(offset + 36, 0, true);
    view.setUint32(offset + 38, 0, true);
    view.setUint32(offset + 42, offsets[i]!, true);
    out.set(e.nameBytes, offset + 46);
    offset += 46 + e.nameBytes.length;
  });

  view.setUint32(offset, 0x06054b50, true);
  view.setUint16(offset + 4, 0, true);
  view.setUint16(offset + 6, 0, true);
  view.setUint16(offset + 8, entries.length, true);
  view.setUint16(offset + 10, entries.length, true);
  view.setUint32(offset + 12, offset - centralStart, true);
  view.setUint32(offset + 16, centralStart, true);
  view.setUint16(offset + 20, 0, true);
  return out;
}
